/** Who is free and who is buried — beside the grid, while the list is being written.
 *
 *  The Team screen answers this after the fact. The decision that sets the load is made
 *  here, choosing a name in the *Who does it* column, and that is the moment the
 *  information is worth having: changing a name in a row costs nothing, moving a filed
 *  task costs a message to somebody.
 *
 *  **Saved and draft are never blended.** "6 open · +3 you are adding" — a number that
 *  quietly mixes work that exists with work you are still typing is a number nobody can
 *  check against another screen.
 *
 *  The draft half is scored with the weights the server sent rather than a copy kept
 *  here, so the rail and the Team screen cannot drift apart the first time either is
 *  tuned.
 */
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, type Workload } from '@/api/client'
import { ErrorNote } from '@/components/ui'

type Draft = { responsible_ids: string[]; approver_ids: string[]
               priority: string }

type Line = {
  user_id: string
  name: string
  open: number
  approving: number
  addedDoing: number
  addedApproving: number
  load: number
  signal: string
  why: string
}

// How many to name before counting the rest, so the line stays a sentence.
const NAMED = 2
// The band is only worth pinning while it stays one line. Filing a day's list
// across a dozen people would otherwise put a dozen chips in it and turn the
// strip into a block sitting over the rows being typed - the exact thing it was
// moved out of the sidebar to avoid. Four, then a count.
const CHIPS = 4


export function WorkloadRail({ rows }: { rows: Draft[] }) {
  const [open, setOpen] = useState(true)

  const q = useQuery({
    queryKey: ['workload'],
    queryFn: () => api.get<Workload>('/reports/workload'),
  })

  const lines = useMemo<Line[]>(() => {
    if (!q.data) return []
    const w = q.data.weights
    const saved = q.data.by_person

    // Everybody the server knows about, plus anybody named in a row that has not been
    // filed - somebody with nothing at all does not appear in the saved figures, and
    // they are exactly who the Owner is looking for.
    // Ids, not names: two people can share a display name, and grouping by it added
    // their work together and reported one person who does not exist.
    const ids = new Set<string>(Object.keys(saved))
    for (const r of rows) {
      r.responsible_ids.forEach((id) => ids.add(id))
      r.approver_ids.forEach((id) => ids.add(id))
    }
    ids.delete('(unassigned)')

    const out: Line[] = []
    for (const id of ids) {
      const s = saved[id]
      // `includes`, not equality: a row with three people counts toward all three,
      // so these stop being a partition of `rows.length` and that is correct.
      const doing = rows.filter((r) => r.responsible_ids.includes(id))
      const approving = rows.filter((r) => r.approver_ids.includes(id)).length
      // The server sends the name beside the figures, and for an Owner it seeds every
      // active person - which is everybody who can be picked in the grid. The id is a
      // last resort that should not be reachable rather than a display choice.
      const name = s?.name || id

      // The server's own arithmetic, applied to rows it cannot see yet. A draft has
      // never been carried, so only the priority weight applies.
      const draftLoad = doing.reduce(
        (n, r) => n + (w.priority[(r.priority || 'Regular').toLowerCase()] ?? 1), 0)

      out.push({
        user_id: id,
        name,
        open: s?.open ?? 0,
        approving: s?.waiting_to_approve ?? 0,
        addedDoing: doing.length,
        addedApproving: approving,
        load: (s?.load ?? 0) + draftLoad,
        signal: s?.signal ?? 'free',
        why: s?.why ?? '',
      })
    }
    // Lightest first: the rail is read to answer "who can take this".
    return out.sort((a, b) => a.load - b.load || a.name.localeCompare(b.name))
  }, [q.data, rows])

  if (q.error) return <ErrorNote error={q.error} />
  if (!q.data) return null

  // Free means nothing at all - not merely no tasks of their own. The server now says
  // the same thing (`_verdict` takes the number waiting), so this is no longer a local
  // correction; it is here because the rail also counts rows that have been typed and
  // not yet filed, which the server cannot see. The load score still excludes
  // approvals on purpose - an approval is work to hand back, not work to do.
  const idle = (l: Line) =>
    l.load === 0 && l.approving === 0 && l.addedApproving === 0
  const withWork = lines.filter((l) => !idle(l))

  // What the dropdown cannot say.
  //
  // Who has room now lives in the *Who does it* options, banded and counted, which is
  // where the choice is actually made - so the rail no longer lists people. It keeps
  // the two things a native <option> has nowhere to put: that the day as a whole is
  // lopsided, and what you have already handed out in rows that are still being typed.
  // The second is the one nobody else has, because the server cannot see them.
  const heavy = withWork.filter((l) => l.signal === 'overloaded').reverse()
  const adding = withWork.filter((l) => l.addedDoing > 0 || l.addedApproving > 0)

  return (
    // A strip, not a column. As a 288px column it took 304px off a 1224px page, and
    // with 832px of fixed columns in the grid the Task field - the one people actually
    // type in - was left about 68px wide, showing a single character. Above the grid it
    // costs one line of height and gives that width back.
    //
    // Sticky, because rows are added by scrolling down and the figures are useless if
    // they scroll away; one line high, because they are useless too if they cover the
    // rows being read. `top-0` is relative to the scrolling <main>.
    // `bg-paper` and the padding are not decoration: a sticky element with a
    // transparent gutter lets rows scroll through the gap and show above it, which
    // reads as a rendering fault. The band has to cover what passes under it.
    // The negative offset matches <main>'s own padding (p-3.5 / sm:p-5). `top-0` pins
    // to the padding edge, which left a strip of grid showing in the gap above the
    // band; pinning into the padding and paying it back as padding-top puts the band
    // flush against the header with nothing sliding past it.
    <div className="sticky -top-3.5 sm:-top-5 z-10 pt-3.5 sm:pt-5 pb-3 bg-paper">
      {/* Two columns, not one wrapping row: the caption and the button hold the
          right edge and never wrap, so what wraps at a narrow width is the reading
          matter, which is the part that can afford a second line. */}
      <div className="card px-3.5 py-2 flex items-start justify-between gap-3">
        <div className="flex items-baseline gap-2.5 flex-wrap min-w-0 py-0.5">
        <b className="text-[12.5px] shrink-0">Who has room</b>

        {open && (
          <span className="text-[12px] text-ink-2">
            {heavy.length > 0 ? (
              <>
                <b>{heavy.slice(0, NAMED).map((l) => l.name).join(', ')}</b>
                {heavy.length > NAMED && ` and ${heavy.length - NAMED} others`}
                {' '}{heavy.length === 1 ? 'is' : 'are'} carrying most — median{' '}
                <b className="mono">{q.data.median_load}</b>
              </>
            ) : withWork.length > 0 ? (
              <>Nobody is obviously overloaded — median{' '}
              <b className="mono">{q.data.median_load}</b></>
            ) : (
              <>Nobody has any work yet</>
            )}
          </span>
        )}

        {/* What is being added right now, inline. The one figure nobody else has: the
            server cannot see rows that have not been filed.

            The word "adding" is said once for the whole group rather than once per
            person. Repeated on each chip it was both noise and the thing that pushed
            the band onto a second line, which defeats pinning it. */}
        {open && adding.length > 0 && (
          <span className="text-[11.5px] text-ink-3">
            · you are adding{' '}
            {adding.slice(0, CHIPS).map((l, i) => (
              <span key={l.name} className="mono">
                {i > 0 && ', '}
                {l.name}{' '}
                <b className="text-primary">
                  +{l.addedDoing || l.addedApproving}
                  {l.addedApproving && !l.addedDoing ? '\u00a0to\u00a0approve' : ''}
                </b>
              </span>
            ))}
            {adding.length > CHIPS && (
              <span className="mono"> and {adding.length - CHIPS} more</span>
            )}
          </span>
        )}

        </div>

        <div className="flex items-center gap-2 shrink-0">
        <span className="text-[10.5px] text-ink-3 hidden lg:inline"
              title={'Open tasks weighted by priority, against the median - the same '
                     + 'figure the Team screen uses. Approvals waiting are counted '
                     + 'beside it rather than folded in, since they are work to hand '
                     + 'back rather than work to do. It counts rows, not hours.'}>
          counts rows, not hours
        </span>
        <button className="btn btn-sm shrink-0" onClick={() => setOpen(!open)}>
          {open ? 'Hide' : 'Show'}
        </button>
        </div>
      </div>
    </div>
  )
}
